// ---------------------------------------------------------
// Datei: ReworkCommentThread.tsx
// Zweck: Kommentar-Thread an einem Nacharbeiten-Ticket (Ticket #1090) — Ops-Manager
//        schreiben Kommentare mit @-Mentions, bearbeiten/löschen eigene Kommentare und
//        können auch offline neue Kommentare anlegen (Sync über die Upload-Queue).
// Kontext:
// - Nur für isOpsManager sichtbar (gleiches Gating-Muster wie DocumentChecklist.tsx:184,
//   "Monteur" ist einfach die Abwesenheit von isOpsManager, kein eigener Rollen-Check).
// - Neuanlage läuft immer über addToQueue('rework_comment_add', ...) — verarbeitet sofort
//   wenn online, wird sonst gequeued (siehe AbschlussberichtWizard.handleSubmit). Der
//   optimistische Kommentar bekommt eine `pending-<timestamp>`-ID, die zugleich als
//   `queueKey` mitgeschickt wird — dadurch ist die IndexedDB-Queue-Item-ID IDENTISCH mit
//   der lokalen Kommentar-ID, was zwei Dinge ermöglicht: (1) präzises Matching zwischen
//   Kommentar und Queue-Eintrag (statt fragilem Text-Vergleich), (2) Bearbeiten/Löschen
//   eines noch wartenden Kommentars kann direkt am Queue-Eintrag ansetzen (siehe
//   docs/nacharbeit-hubspot-kommentare-plan.md, Abschnitt 4 "Offline-Fähigkeit") — kein
//   API-Call nötig, da noch keine echte HubSpot-Note existiert. Nur ein bereits
//   synchronisierter Kommentar läuft für Bearbeiten/Löschen online-only direkt gegen die
//   API, weil er dafür seine echte, von HubSpot vergebene Kommentar-ID braucht.
// - Sobald ein wartendes Queue-Item verarbeitet wurde (sofort online oder später beim
//   Reconnect), erkennt ein Effekt das Verschwinden aus `queueItems` und unterscheidet
//   Erfolg/Fehlschlag über das globale `project-status-refresh`-CustomEvent (wird von
//   queueService.processQueue nur für tatsächlich erfolgreiche Items gefeuert — siehe
//   notifyProjectStatusRefresh). Bei Erfolg wird die Kommentarliste neu geladen und der
//   optimistische Eintrag durch den echten Server-Kommentar (echte ID) ersetzt; erst dann
//   erscheint der Erfolgs-Toast. Bei endgültigem Fehlschlag (Item verschwunden, aber nicht
//   als erfolgreich gemeldet — z.B. dauerhaft verworfener 4xx-Fehler) wird der optimistische
//   Eintrag entfernt und ein Fehler-Toast gezeigt, statt fälschlich "gespeichert" zu melden.
// - react-mentions liefert im onChange sowohl die interne Markup- als auch die reine
//   Klartext-Variante: `text` (Klartext, Mentions bereits als "@Name" inline) geht als
//   lesbarer Anzeigetext an die API, `mentionedOwnerIds` separat als HubSpot-Owner-IDs —
//   daraus baut die One360-API die eigentliche HubSpot-Mention. Beim Start des Bearbeitens
//   wird aus Klartext + mentionedOwnerIds + der geladenen Owner-Liste wieder der von
//   react-mentions erwartete Markup-Wert (`@[Name](ownerId)`) rekonstruiert (siehe
//   buildMentionMarkup) — sonst kennt MentionsInput die bestehenden Mentions nicht und die
//   erste Nutzereingabe würde alle bisherigen Mention-Zuordnungen aus dem State werfen.
// Stand: 25.08.2026
// ---------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import type { MentionsInputStyle, OnChangeHandlerFunc, SuggestionDataItem } from 'react-mentions';
import { useAuth } from '../contexts/AuthContext';
import { useQueue } from '../contexts/QueueContext';
import { useToast } from '../contexts/ToastContext';
import { isQueueItemInFlight, QueueItemLockTimeoutError } from '../services/queueService';
import {
  getReworkCommentsAsync,
  getHubSpotOwnersAsync,
  updateReworkCommentAsync,
  deleteReworkCommentAsync,
} from '../services/api/hubspot';
import type { ReworkComment, HubSpotOwnerListItem } from '../types/hubspot';
import type { ReworkCommentAddQueueData, ProjectStatusRefreshDetail } from '../interfaces/queue';

interface ReworkCommentThreadProps {
  ticketId: string;
  /** Entspricht dem isExpanded-State der Elternkomponente — Daten werden erst geladen,
   *  sobald der Thread sichtbar wird, damit nicht jedes eingeklappte Ticket in einer
   *  langen Liste unnötig Kommentare/Owner lädt. */
  active: boolean;
}

// Client-generierte IDs für optimistisch angehängte, noch nicht synchronisierte
// Kommentare — daran erkennbar, dass es noch keine echte HubSpot-Kommentar-ID gibt.
const PENDING_ID_PREFIX = 'pending-';

const mentionsInputStyle: MentionsInputStyle = {
  control: {
    fontSize: 14,
    fontFamily: 'inherit',
  },
  highlighter: {
    padding: 9,
    border: '1px solid transparent',
    minHeight: 64,
  },
  input: {
    padding: 9,
    minHeight: 64,
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    outline: 'none',
  },
  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '1px solid #cbd5e1',
      borderRadius: 8,
      fontSize: 13,
      maxHeight: 240,
      overflowY: 'auto',
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    },
    item: {
      padding: '6px 10px',
      borderBottom: '1px solid #e2e8f0',
      '&focused': {
        backgroundColor: '#e0f2fe',
      },
    },
  },
};

const ownerDisplayName = (owner: HubSpotOwnerListItem): string => {
  const name = [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim();
  return name || owner.email;
};

const commentAuthorLabel = (comment: ReworkComment): string =>
  comment.authorName?.trim() || comment.authorEmail?.trim() || 'Unbekannt';

const formatTimestamp = (iso: string): string => new Date(iso).toLocaleString('de-DE');

interface ResolvedMention {
  id: string;
  display: string;
}

// Baut aus mentionedOwnerIds nur die im aktuellen ownersById-Snapshot noch auflösbaren
// Mentions (archivierte/gelöschte Owner bleiben bewusst unformatierter Klartext statt
// geraten zu werden) und daraus ein Alternation-Pattern, mit dem sich "@Name"-Vorkommen
// im Klartext wiederfinden lassen. Längere Namen zuerst, damit z.B. "@Max" nicht bereits
// als Teilstring von "@Max Mustermann" matcht. Gemeinsame Basis für renderCommentText
// (Hervorhebung beim Anzeigen) und buildMentionMarkup (Markup-Rekonstruktion fürs
// Bearbeiten) — beide sollen dieselben "@Name"-Vorkommen finden, nicht zwei
// unterschiedliche, potenziell abweichende Regex-Aufbauten pflegen.
//
// `byName` ist pro Anzeigename eine LISTE (nicht ein einzelner Eintrag), weil zwei
// unterschiedliche Owner denselben Anzeigenamen haben können (z.B. zwei "Max Mustermann").
// Ohne diese Liste würde ein späterer, gleichnamiger Eintrag in mentionedOwnerIds einen
// früheren beim Aufbau überschreiben, und buildMentionMarkup hätte beim Editieren beide
// Textvorkommen fälschlich derselben ID zugeordnet (PR-Review-Finding). Die Reihenfolge
// der Liste entspricht der Reihenfolge in mentionedOwnerIds; Konsumenten, die die ID pro
// Vorkommen brauchen (buildMentionMarkup), entnehmen sie der Liste in Text-Auftrittsreihenfolge
// (per .shift()) — bei identischem Anzeigenamen ist "erstes Textvorkommen = erster
// mentionedOwnerIds-Eintrag" die bestmögliche Annahme ohne Positionsdaten aus dem Backend.
export const resolveMentionMatches = (
  mentionedOwnerIds: string[],
  ownersById: Map<string, HubSpotOwnerListItem>
): { regex: RegExp; byName: Map<string, ResolvedMention[]> } | null => {
  const byName = new Map<string, ResolvedMention[]>();
  mentionedOwnerIds.forEach((id) => {
    const owner = ownersById.get(id);
    if (owner) {
      const display = ownerDisplayName(owner);
      const key = `@${display}`;
      const existing = byName.get(key);
      if (existing) {
        existing.push({ id, display });
      } else {
        byName.set(key, [{ id, display }]);
      }
    }
  });

  if (byName.size === 0) return null;

  const pattern = Array.from(byName.keys())
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  return { regex: new RegExp(`(${pattern})`, 'g'), byName };
};

// Hebt "@Name"-Vorkommen im Kommentartext hervor, sofern der Name über mentionedOwnerIds
// einer bekannten Owner-Anzeige zugeordnet werden kann. Unbekannte/archivierte Owner
// (nicht mehr in ownersById) bleiben bewusst unformatiert statt geraten zu werden. Reine
// Hervorhebung braucht keine Owner-ID pro Vorkommen (anders als buildMentionMarkup) — bei
// gleichnamigen Owners werden hier deshalb bewusst ALLE Vorkommen hervorgehoben, ohne die
// byName-Liste zu konsumieren.
const renderCommentText = (
  text: string,
  mentionedOwnerIds: string[],
  ownersById: Map<string, HubSpotOwnerListItem>
): React.ReactNode => {
  const matches = resolveMentionMatches(mentionedOwnerIds, ownersById);
  if (!matches) return text;

  const parts = text.split(matches.regex);

  return parts.map((part, index) =>
    matches.byName.has(part) ? (
      <span key={index} className="font-medium text-cyan-700 dark:text-cyan-400">
        {part}
      </span>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );
};

// Rekonstruiert aus Klartext + mentionedOwnerIds den von react-mentions erwarteten
// Markup-Wert (Default-Format `@[__display__](__id__)`) für den `value`-Prop von
// MentionsInput. Ohne das erkennt react-mentions bestehende Mentions beim Editieren
// nicht, und die erste Nutzereingabe würde sie aus editMentionedIds werfen (PR-Review-
// Finding). Nicht mehr auflösbare Owner bleiben unverändert als Klartext stehen — gleiche
// Graceful-Degradation wie renderCommentText.
//
// Anders als renderCommentText MUSS hier pro Textvorkommen eine konkrete Owner-ID
// zugeordnet werden. Bei mehreren gleichnamigen Owners wird die byName-Liste per shift()
// in Text-Auftrittsreihenfolge konsumiert (PR-Review-Finding — vorher bekam JEDES
// Vorkommen desselben Namens dieselbe, zuletzt registrierte ID, sodass eine der beiden
// Mentions beim Speichern stillschweigend verloren ging).
export const buildMentionMarkup = (
  text: string,
  mentionedOwnerIds: string[],
  ownersById: Map<string, HubSpotOwnerListItem>
): string => {
  const matches = resolveMentionMatches(mentionedOwnerIds, ownersById);
  if (!matches) return text;

  const parts = text.split(matches.regex);

  return parts
    .map((part) => {
      const candidates = matches.byName.get(part);
      const mention = candidates?.shift();
      return mention ? `@[${mention.display}](${mention.id})` : part;
    })
    .join('');
};

interface MentionComposerProps {
  value: string;
  onChange: OnChangeHandlerFunc;
  suggestions: SuggestionDataItem[];
  placeholder?: string;
  autoFocus?: boolean;
}

const MentionComposer: React.FC<MentionComposerProps> = ({ value, onChange, suggestions, placeholder, autoFocus }) => (
  <MentionsInput
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    style={mentionsInputStyle}
    autoFocus={autoFocus}
    a11ySuggestionsListLabel="Vorgeschlagene Personen"
  >
    <Mention
      trigger="@"
      data={suggestions}
      displayTransform={(_id, display) => `@${display}`}
      appendSpaceOnAdd
      style={{ backgroundColor: '#cffafe', borderRadius: 4 }}
    />
  </MentionsInput>
);

const ReworkCommentThread: React.FC<ReworkCommentThreadProps> = ({ ticketId, active }) => {
  const { user } = useAuth();
  const { addToQueue, removeFromQueue, isOnline, items: queueItems } = useQueue();
  const { showToast } = useToast();

  // IDs von rework_comment_add-Queue-Items, die laut `project-status-refresh`-Event
  // bereits erfolgreich synchronisiert wurden (siehe queueService.notifyProjectStatusRefresh).
  // Wird vom Deferred-Beobachtungs-Effekt unten konsumiert, um Erfolg von endgültigem
  // Fehlschlag zu unterscheiden (beide äußern sich gleich: Item verschwindet aus queueItems).
  const succeededQueueIdsRef = useRef<Set<string>>(new Set());

  // tempId -> echte, vom Server vergebene Kommentar-ID (aus dem project-status-refresh-Event,
  // siehe queueService.processReworkCommentAdd/notifyProjectStatusRefresh). PR-Review-Finding:
  // eine nachträgliche Zuordnung per Inhalt (Text+Autor-E-Mail) ist NICHT eindeutig — z.B. bei
  // zwei gleichlautenden Kommentaren desselben Ops-Users hätte confirmSuccess sonst den
  // falschen bestehenden Kommentar für einen migrierten Edit-Entwurf treffen können. Wird von
  // confirmSuccess konsumiert, um die offene Bearbeitung zuverlässig auf die echte ID zu
  // migrieren, statt sie zu erraten.
  const realCommentIdByTempIdRef = useRef<Map<string, string>>(new Map());

  // IDs pending-* Kommentare, deren addToQueue-Aufruf NICHT sofort verarbeitet wurde
  // (addToQueue löste mit `undefined` auf — offline, oder eine andere Verarbeitung lief
  // gerade). NUR für diese IDs ist "nicht (mehr) in queueItems" ein verlässliches Sync-
  // Ende-Signal; für alle anderen (sofort online verarbeiteten) IDs kennt handleSubmitComment
  // das Ergebnis bereits direkt aus addToQueues Rückgabewert (siehe PR-Review-Finding:
  // ein reiner "ist die ID noch in queueItems"-Check kann direkt nach dem Absenden nicht
  // zwischen "noch nicht registriert" und "schon fertig verarbeitet" unterscheiden).
  const deferredPendingIdsRef = useRef<Set<string>>(new Set());

  // Ref-Spiegel von `queueItems`, damit der Initial-Fetch-Effekt unten den *aktuellen*
  // Queue-Stand lesen kann, ohne selbst von `queueItems` abzuhängen (sonst würde jede
  // Queue-Änderung anderswo in der App einen erneuten Fetch auslösen).
  const queueItemsRef = useRef(queueItems);
  useEffect(() => {
    queueItemsRef.current = queueItems;
  }, [queueItems]);

  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<ReworkComment[]>([]);
  const [owners, setOwners] = useState<HubSpotOwnerListItem[]>([]);
  const hasFetchedRef = useRef(false);

  const [composeValue, setComposeValue] = useState('');
  const [composePlainText, setComposePlainText] = useState('');
  const [composeMentionedIds, setComposeMentionedIds] = useState<string[]>([]);

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editPlainText, setEditPlainText] = useState('');
  const [editMentionedIds, setEditMentionedIds] = useState<string[]>([]);
  const [savingEditId, setSavingEditId] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  const effectiveIsOpsManager = Boolean(user?.isOpsManager);

  useEffect(() => {
    if (!active || !effectiveIsOpsManager || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    // Rehydrierung (PR-Review-Finding, 2. Fassung): läuft jetzt SOFORT und komplett
    // unabhängig vom Server-GET, statt in dessen .then()-Zweig zu hängen. Grund: im
    // eigentlichen Zielszenario — offline absenden → Ticket einklappen/unmounten (siehe
    // PhasePickerScreen, rendert ReworkCommentThread nur innerhalb `{isExpanded && ...}` —
    // dabei geht jeglicher lokale State inkl. deferredPendingIdsRef verloren) → OFFLINE
    // wieder öffnen — rejected getReworkCommentsAsync (kein Netz), der alte .then()-Zweig
    // lief dann gar nicht erst, der wartende Kommentar blieb unsichtbar. Ein noch wartender
    // rework_comment_add-Eintrag für DIESES Ticket existiert ausschließlich in der Queue
    // (server-seitig noch nicht angelegt) und muss daher ohnehin nicht mit einem
    // Server-Ergebnis abgeglichen werden — er kann sofort angezeigt werden.
    const queuedForTicket = queueItemsRef.current.filter(
      (item) =>
        item.type === 'rework_comment_add' &&
        (item.data as ReworkCommentAddQueueData | undefined)?.ticketId === ticketId
    );
    const rehydrated: ReworkComment[] = queuedForTicket.map((item) => {
      deferredPendingIdsRef.current.add(item.id);
      const data = item.data as ReworkCommentAddQueueData;
      const createdAt = item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt);
      return {
        id: item.id,
        ticketId,
        text: data.text,
        authorEmail: user?.email,
        authorName: user?.name,
        mentionedOwnerIds: data.mentionedOwnerIds ?? [],
        createdAt: createdAt.toISOString(),
      };
    });
    if (rehydrated.length > 0) {
      setComments(rehydrated);
    }

    setLoading(true);
    // Bewusst zwei unabhängige Fetches statt Promise.all: getHubSpotOwnersAsync wirft jetzt
    // ebenfalls bei einem Fehler (PR-Review-Finding — vorher schluckte es Fehler lautlos zu
    // `[]`, ohne jede Rückmeldung). Mit Promise.all würde ein reiner Owner-Ladefehler den
    // gesamten Block in den .catch-Zweig reißen und damit auch bereits erfolgreich geladene
    // Kommentare verwerfen — Kommentare und Owner sollen unabhängig voneinander fehlschlagen
    // können (Mentions/Hervorhebung degradieren dann nur, die Kommentarliste bleibt nutzbar).
    getReworkCommentsAsync(ticketId)
      .then((commentsResult) => {
        // Mit den oben bereits rehydrierten, weiterhin wartenden Einträgen MERGEN statt sie
        // zu überschreiben — ein Eintrag kann zwischenzeitlich synchronisiert worden sein
        // (dann taucht er unter der echten ID in commentsResult auf; die alte pending-Kopie
        // verschwindet, sobald der Deferred-Beobachtungs-Effekt unten das Verschwinden aus
        // queueItems registriert, nicht schon hier).
        setComments((prev) => {
          const stillPendingOptimistic = prev.filter((comment) => comment.id.startsWith(PENDING_ID_PREFIX));
          return [...commentsResult, ...stillPendingOptimistic];
        });
      })
      .catch(() => {
        // getReworkCommentsAsync wirft jetzt statt auf `[]` zu degradieren (siehe dortige
        // Doku). Die oben bereits gesetzten rehydrierten Pending-Kommentare bleiben dabei
        // unangetastet stehen (kein Datenverlust) — nur ein erneuter Ladeversuch beim
        // nächsten Öffnen ist nötig, um den Rest der (echten) Kommentare zu bekommen.
        hasFetchedRef.current = false;
        showToast('error', 'Kommentare konnten nicht geladen werden', 4000);
      })
      .finally(() => setLoading(false));

    getHubSpotOwnersAsync()
      .then(setOwners)
      .catch(() => {
        setOwners([]);
        showToast('error', 'HubSpot-User konnten nicht geladen werden — @-Mentions eingeschränkt', 4000);
      });
  }, [active, effectiveIsOpsManager, ticketId, showToast, user?.email, user?.name]);

  // Merkt sich, welche rework_comment_add-Queue-Items laut globalem Event erfolgreich
  // synchronisiert wurden — queueService.processQueue feuert dieses Event ausschließlich
  // für tatsächlich erfolgreiche Items (nicht für dauerhaft verworfene 4xx-Fehler). Für
  // sofort (online) verarbeitete Items kennt handleSubmitComment das Ergebnis bereits
  // direkt aus addToQueues Rückgabewert und braucht dieses Event nicht — es ist nur für
  // die per deferredPendingIdsRef beobachteten, verzögert verarbeiteten Items relevant.
  useEffect(() => {
    const handleStatusRefresh = (event: Event) => {
      const detail = (event as CustomEvent<ProjectStatusRefreshDetail>).detail;
      detail?.successfulItems?.forEach((refreshedItem) => {
        if (refreshedItem.type === 'rework_comment_add') {
          succeededQueueIdsRef.current.add(refreshedItem.id);
          if (refreshedItem.resultId) {
            realCommentIdByTempIdRef.current.set(refreshedItem.id, refreshedItem.resultId);
          }
        }
      });
    };
    window.addEventListener('project-status-refresh', handleStatusRefresh);
    return () => window.removeEventListener('project-status-refresh', handleStatusRefresh);
  }, []);

  // Ersetzt einen bestätigt synchronisierten optimistischen Eintrag durch die echten
  // Server-Kommentare (echte HubSpot-ID → Bearbeiten/Löschen funktionieren sofort, ohne
  // Reload). getReworkCommentsAsync wirft jetzt bei einem Ladefehler (statt auf `[]` zu
  // degradieren) — nur so lässt sich ein Ladefehler von "Ticket hat wirklich keine
  // Kommentare mehr" unterscheiden; ein Fehlschlag hier hieße sonst, dass sämtliche
  // bereits angezeigten echten Kommentare durch eine fälschlich leere Liste ersetzt würden
  // (PR-Review-Finding). Bei einem Ladefehler bleibt der optimistische Eintrag unverändert
  // stehen (kein Datenverlust, der Kommentar wurde serverseitig bereits angelegt) und alle
  // anderen bereits geladenen Kommentare bleiben ebenfalls unangetastet.
  const confirmSuccess = useCallback(
    async (tempId: string) => {
      // Wenn der Nutzer genau diesen (noch pending) Kommentar gerade bearbeitet, während der
      // Hintergrund-Sync für das ORIGINAL (vor dem Edit) abschließt, darf der Entwurf nicht
      // verloren gehen — weder hier noch (das war der vorherige Fix) beim blockierten
      // Speicher-Versuch in handleSaveEdit selbst.
      const wasBeingEdited = editingCommentId === tempId;
      // PR-Review-Finding (2. Fassung): das Umhängen auf die echte ID darf NICHT am
      // nachfolgenden GET hängen. Liefert HubSpot die gerade erzeugte Note dort noch nicht
      // zurück (Eventual Consistency) oder schlägt der GET ganz fehl, blieb tempId bisher
      // "pending" — ein erneutes "Speichern" hätte handleSaveEdit dann wieder als Neuanlage
      // (rework_comment_add) statt als Update behandelt und einen zweiten Kommentar erzeugt.
      // Die echte ID ist aus dem project-status-refresh-Event bereits bekannt (siehe
      // queueService.processReworkCommentAdd) — das Umhängen von `comments`/`editingCommentId`
      // passiert deshalb SOFORT und synchron, unabhängig vom GET-Ausgang. Der GET danach
      // aktualisiert nur noch den Rest der Liste (Inhalt/Reihenfolge/andere Kommentare).
      const realId = realCommentIdByTempIdRef.current.get(tempId);

      // Re-Keying (falls die echte ID bekannt ist) passiert unabhängig davon, ob gerade
      // editiert wird — auch für einen unbearbeiteten pending-Kommentar sollen Bearbeiten/
      // Löschen sofort verfügbar sein, ohne auf den nachfolgenden GET warten zu müssen.
      if (realId) {
        realCommentIdByTempIdRef.current.delete(tempId);
        setComments((prev) => prev.map((comment) => (comment.id === tempId ? { ...comment, id: realId } : comment)));
      }

      if (wasBeingEdited) {
        if (realId) {
          setEditingCommentId(realId);
          showToast('info', 'Kommentar wurde synchronisiert — dein Entwurf kann jetzt gespeichert werden', 5000);
        } else {
          // Keine echte ID aus dem Event bekannt — sollte praktisch nicht vorkommen (das
          // Event löst succeededQueueIdsRef aus, das confirmSuccess erst auslöst), aber
          // defensiv: den Entwurf lieber sichtbar verwerfen und klar kommunizieren, als ihn
          // ohne verlässliche Zuordnung stehen zu lassen.
          setEditingCommentId(null);
          setEditValue('');
          setEditPlainText('');
          setEditMentionedIds([]);
          showToast('info', 'Kommentar wurde synchronisiert — bitte deine Änderung erneut speichern', 5000);
        }
      } else {
        showToast('success', 'Kommentar gespeichert', 3000);
      }

      try {
        const fresh = await getReworkCommentsAsync(ticketId);
        setComments((prev) => {
          // PR-Review-Finding: NICHT pauschal jeden lokalen Kommentar behalten, der (noch)
          // nicht in `fresh` auftaucht — das hätte auch einen zwischenzeitlich von einem
          // anderen Ops-User oder direkt in HubSpot gelöschten Kommentar als "Ghost" wieder
          // einfügen können (der GET liefert ihn korrekt nicht mehr, ein zu weiter Filter
          // holt ihn aber aus `prev` zurück). Nur zwei Fälle rechtfertigen das Behalten trotz
          // Fehlens in `fresh`: (1) weiterhin wartende, noch nicht synchronisierte
          // Kommentare (pending-Präfix) und (2) genau der oben in DIESEM Aufruf gerade erst
          // auf `realId` umgehängte Kommentar, den `fresh` mangels Eventual-Consistency oder
          // GET-Fehlschlag noch nicht enthalten muss. Alles andere gilt als vom Server
          // autoritativ nicht mehr vorhanden.
          const keepDespiteMissingFromFresh = prev.filter(
            (comment) => comment.id.startsWith(PENDING_ID_PREFIX) || comment.id === realId
          );
          const freshIds = new Set(fresh.map((comment) => comment.id));
          return [...fresh, ...keepDespiteMissingFromFresh.filter((comment) => !freshIds.has(comment.id))];
        });
      } catch {
        // Ladefehler: das oben bereits vorgenommene Umhängen der ID (und ggf. der Editor-
        // Zustand) bleibt unberührt — nur der Rest der Liste wird beim nächsten Laden
        // aktualisiert, kein Datenverlust.
      }
    },
    [ticketId, showToast, editingCommentId]
  );

  // Beobachtet ausschließlich verzögert (nicht sofort online) verarbeitete Items — siehe
  // deferredPendingIdsRef-Doku oben. Verschwindet eine dieser IDs aus `queueItems` (Sync
  // fertig, egal ob erst beim späteren Reconnect oder über den manuellen Sync-Button), wird
  // über succeededQueueIdsRef zwischen Erfolg und endgültigem Fehlschlag unterschieden —
  // ein Item, das nur wegen eines transienten Fehlers erneut versucht wird, bleibt
  // weiterhin in `queueItems` stehen und landet hier gar nicht erst.
  useEffect(() => {
    if (deferredPendingIdsRef.current.size === 0) return;

    const succeededIds: string[] = [];
    const failedIds: string[] = [];

    deferredPendingIdsRef.current.forEach((id) => {
      const stillQueued = queueItems.some((queueItem) => queueItem.id === id && queueItem.type === 'rework_comment_add');
      if (stillQueued) return;

      deferredPendingIdsRef.current.delete(id);
      if (succeededQueueIdsRef.current.has(id)) {
        succeededQueueIdsRef.current.delete(id);
        succeededIds.push(id);
      } else {
        failedIds.push(id);
      }
    });

    if (failedIds.length > 0) {
      setComments((prev) => prev.filter((comment) => !failedIds.includes(comment.id)));
      setEditingCommentId((prev) => (prev && failedIds.includes(prev) ? null : prev));
      showToast('error', 'Kommentar konnte nicht gespeichert werden', 4000);
    }

    succeededIds.forEach((id) => void confirmSuccess(id));
  }, [queueItems, showToast, confirmSuccess]);

  const ownersById = useMemo(() => new Map(owners.map((owner) => [owner.id, owner])), [owners]);
  const ownerSuggestions: SuggestionDataItem[] = useMemo(
    () =>
      owners
        .filter((owner) => !owner.archived)
        .map((owner) => ({ id: owner.id, display: ownerDisplayName(owner) })),
    [owners]
  );

  const handleComposeChange: OnChangeHandlerFunc = (_event, newValue, newPlainTextValue, mentions) => {
    setComposeValue(newValue);
    setComposePlainText(newPlainTextValue);
    setComposeMentionedIds(mentions.map((mention) => String(mention.id)));
  };

  const handleSubmitComment = () => {
    const text = composePlainText.trim();
    if (!text) return;

    const tempId = `${PENDING_ID_PREFIX}${Date.now()}`;
    const optimisticComment: ReworkComment = {
      id: tempId,
      ticketId,
      text,
      authorEmail: user?.email,
      authorName: user?.name,
      mentionedOwnerIds: composeMentionedIds,
      createdAt: new Date().toISOString(),
    };

    setComments((prev) => [...prev, optimisticComment]);
    setComposeValue('');
    setComposePlainText('');
    setComposeMentionedIds([]);

    // Kein sofortiger "Kommentar gespeichert"-Erfolgs-Toast mehr, wenn online: der bisherige
    // Code hätte bei einem echten API-Fehler (z.B. 403) trotzdem "gespeichert" gezeigt. Der
    // Offline-Hinweis bleibt unverändert, weil er unabhängig vom Sync-Ausgang stimmt: der
    // Kommentar landet in jedem Fall erstmal nur in der Queue.
    if (!isOnline) {
      showToast('info', 'Kommentar wurde offline in die Queue gelegt', 4000);
    }

    // queueKey = tempId: macht die IndexedDB-Queue-Item-ID identisch mit der lokalen
    // Kommentar-ID — Grundlage fürs direkte Bearbeiten/Löschen eines noch wartenden
    // Kommentars (siehe handleSaveEdit/handleDelete) und fürs Matching unten.
    //
    // addToQueue löst jetzt mit dem tatsächlichen Verarbeitungsergebnis auf (siehe
    // QueueContext), statt nur "registriert" zu bedeuten (PR-Review-Finding: ein reiner
    // Blick auf `queueItems` direkt nach dem Absenden kann nicht zwischen "noch nicht
    // registriert" und "schon fertig verarbeitet" unterscheiden — genau diese Mehrdeutigkeit
    // führte zu einem False-Positive-Fehlschlag). Drei Fälle:
    // - result enthält tempId in `successful`: sofort online erfolgreich verarbeitet ->
    //   direkt bestätigen, kein Warten auf ein Event nötig.
    // - result enthält tempId in `failed`: sofort erfolglos verarbeitet (z.B. dauerhaft
    //   verworfener 4xx-Fehler) -> direkt als Fehlschlag behandeln.
    // - result ist `undefined`: NICHT sofort verarbeitet (offline, oder gerade läuft schon
    //   eine andere Verarbeitung) -> das Item liegt jetzt in der Queue, der Ausgang wird
    //   erst später über deferredPendingIdsRef + das project-status-refresh-Event bekannt.
    //
    // WICHTIG (PR-Review-Finding): `result.failed` bedeutet NICHT zwangsläufig "endgültig
    // verworfen" — processQueue legt dort auch Items ab, die nach einem transienten Fehler
    // (Netzwerk, 5xx, Timeout, o.ä.) mit erhöhtem Retry-Zähler weiterhin in der Queue liegen
    // und später automatisch erneut versucht werden. Nur `result.permanentlyFailed` (Retries
    // erschöpft oder ein echter 4xx) bedeutet "wirklich weg". Ein transient fehlgeschlagenes
    // Item wird deshalb genauso wie ein deferred registriertes behandelt (weiter beobachten),
    // statt den gerade erst abgesendeten Kommentar aus der UI zu entfernen, obwohl er im
    // Hintergrund noch erfolgreich synchronisieren kann.
    void addToQueue('rework_comment_add', {
      queueKey: tempId,
      ticketId,
      text,
      mentionedOwnerIds: composeMentionedIds,
    } satisfies ReworkCommentAddQueueData)
      .then((result) => {
        if (!result || (result.failed.includes(tempId) && !result.permanentlyFailed.includes(tempId))) {
          deferredPendingIdsRef.current.add(tempId);
          return;
        }
        if (result.successful.includes(tempId)) {
          void confirmSuccess(tempId);
        } else {
          setComments((prev) => prev.filter((comment) => comment.id !== tempId));
          showToast('error', 'Kommentar konnte nicht gespeichert werden', 4000);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof QueueItemLockTimeoutError) {
          // Ein Lock-Timeout ist ein UNBESTIMMTER Ausgang, kein definitiver Fehlschlag (siehe
          // QueueItemLockTimeoutError-Doku in queueService.ts) - addQueueItem läuft im
          // Hintergrund weiter und kann den Kommentar noch erfolgreich schreiben. Genau wie im
          // undefined-Fall oben bleibt der optimistische Kommentar bestehen statt entfernt zu
          // werden (sonst würde ein erneutes Absenden mit neuer pending-ID einen Duplikat-
          // Kommentar erzeugen, PR-Review-Finding #976/#422) und wird über das
          // project-status-refresh-Event final bestätigt oder verworfen.
          console.warn('[ReworkCommentThread] Kommentar-Vormerkung hängt noch (Lock-Timeout):', err);
          deferredPendingIdsRef.current.add(tempId);
          return;
        }
        setComments((prev) => prev.filter((comment) => comment.id !== tempId));
        const message = err instanceof Error ? err.message : 'Kommentar konnte nicht gespeichert werden';
        showToast('error', message, 4000);
      });
  };

  const startEdit = (comment: ReworkComment) => {
    setEditingCommentId(comment.id);
    // MentionsInput braucht als kontrollierten Wert den react-mentions-Markup-Wert
    // (`@[Name](ownerId)`), nicht den Klartext aus comment.text — sonst kennt die
    // Bibliothek die bestehenden Mentions nicht, und die erste Nutzereingabe würde sie
    // aus editMentionedIds werfen (siehe buildMentionMarkup, PR-Review-Finding).
    setEditValue(buildMentionMarkup(comment.text, comment.mentionedOwnerIds, ownersById));
    setEditPlainText(comment.text);
    setEditMentionedIds(comment.mentionedOwnerIds);
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditValue('');
    setEditPlainText('');
    setEditMentionedIds([]);
  };

  const handleEditChange: OnChangeHandlerFunc = (_event, newValue, newPlainTextValue, mentions) => {
    setEditValue(newValue);
    setEditPlainText(newPlainTextValue);
    setEditMentionedIds(mentions.map((mention) => String(mention.id)));
  };

  const handleSaveEdit = async (comment: ReworkComment) => {
    const text = editPlainText.trim();
    if (!text) return;
    const commentId = comment.id;
    const isPending = commentId.startsWith(PENDING_ID_PREFIX);
    setSavingEditId(commentId);
    try {
      if (isPending) {
        // PR-Review-Finding: ist der Queue-Eintrag genau JETZT mitten in einem laufenden
        // Sync-Versuch (processQueue hat den Request mit der ALTEN Payload bereits
        // abgeschickt, wartet auf Antwort), darf er hier NICHT überschrieben werden. Würde
        // der laufende Original-Request danach erfolgreich sein, entfernt processQueue das
        // Item per removeQueueItem — inklusive des gerade erst überschriebenen, nie
        // gesendeten Edits, der dadurch spurlos verloren ginge. Der eingegebene Text bleibt
        // im Formular stehen (kein Verwerfen), der Nutzer muss nur kurz erneut speichern.
        if (isQueueItemInFlight(commentId)) {
          showToast('info', 'Kommentar wird gerade synchronisiert — bitte kurz warten und erneut speichern', 5000);
          return;
        }
        // Noch nicht synchronisierter Kommentar: Es gibt noch keine echte HubSpot-Note,
        // also kein API-Call — stattdessen wird der wartende Queue-Eintrag direkt
        // überschrieben. queueKey === commentId (siehe handleSubmitComment) sorgt dafür,
        // dass addToQueue dasselbe IndexedDB-Item unter derselben ID aktualisiert statt
        // ein zweites, dupliziertes Queue-Item anzulegen (bestehendes addToQueue-Verhalten,
        // siehe QueueContext.tsx). Ist der Client online, versucht addToQueue direkt im
        // Anschluss zu synchronisieren — der Sync-Bestätigungs-Effekt oben übernimmt dann
        // wie bei der Neuanlage die Erfolgs-/Fehler-Behandlung.
        await addToQueue('rework_comment_add', {
          queueKey: commentId,
          ticketId,
          text,
          mentionedOwnerIds: editMentionedIds,
        } satisfies ReworkCommentAddQueueData);
        setComments((prev) =>
          prev.map((c) => (c.id === commentId ? { ...c, text, mentionedOwnerIds: editMentionedIds } : c))
        );
        cancelEdit();
      } else {
        const updated = await updateReworkCommentAsync(ticketId, commentId, {
          text,
          mentionedOwnerIds: editMentionedIds,
        });
        setComments((prev) => prev.map((c) => (c.id === commentId ? updated : c)));
        cancelEdit();
        showToast('success', 'Kommentar aktualisiert', 3000);
      }
    } catch (err: unknown) {
      if (err instanceof QueueItemLockTimeoutError) {
        // Ein Lock-Timeout ist ein UNBESTIMMTER Ausgang, kein definitiver Fehlschlag (siehe
        // QueueItemLockTimeoutError-Doku in queueService.ts) - der Text wurde bereits per
        // addToQueue geschrieben oder wird es im Hintergrund noch. Bearbeitungsmodus trotzdem
        // verlassen (kein Datenverlust, kein Retry-Pfad hier), nur ohne Fehler-Toast, der
        // fälschlich einen definitiven Fehlschlag suggerieren würde (PR-Review-Finding #976/#422).
        console.warn('[ReworkCommentThread] Kommentar-Bearbeitung hängt noch (Lock-Timeout):', err);
        showToast('info', 'Kommentar wird noch synchronisiert', 3000);
        cancelEdit();
      } else {
        const message = err instanceof Error ? err.message : 'Kommentar konnte nicht aktualisiert werden';
        showToast('error', message, 4000);
      }
    } finally {
      setSavingEditId(null);
    }
  };

  const handleDelete = async (comment: ReworkComment) => {
    const confirmed = window.confirm('Kommentar wirklich löschen?');
    if (!confirmed) return;
    const commentId = comment.id;
    setDeletingCommentId(commentId);
    try {
      if (commentId.startsWith(PENDING_ID_PREFIX)) {
        // Noch nicht synchronisiert: kein echter Kommentar auf dem Server vorhanden, also
        // kein deleteReworkCommentAsync-Call — stattdessen wird der wartende Queue-Eintrag
        // lokal entfernt und der optimistische Kommentar direkt aus dem State genommen.
        // Wichtig: erst aus deferredPendingIdsRef nehmen, DANN das Queue-Item entfernen —
        // sonst könnte der Deferred-Beobachtungs-Effekt oben das durch removeFromQueue
        // ausgelöste Verschwinden aus `queueItems` fälschlich als endgültigen Fehlschlag
        // werten (die ID wäre zu diesem Zeitpunkt noch in deferredPendingIdsRef, aber schon
        // nicht mehr in `queueItems`) und einen Fehler-Toast für ein absichtliches Löschen
        // zeigen. Nur relevant, falls der Kommentar überhaupt verzögert (nicht sofort
        // online) angelegt wurde — bei einem sofort verarbeiteten Kommentar wäre er längst
        // kein pending-* mehr und diese Branch würde gar nicht erst laufen.
        deferredPendingIdsRef.current.delete(commentId);
        // Die übrigen Refs und der sichtbare Kommentar werden dagegen erst NACH erfolgreicher
        // Löschung bereinigt (Code-Review-Finding, Re-Review): removeFromQueue kann jetzt bei
        // einem Persistenzfehler werfen (siehe queueService.ts) - schlägt die Löschung fehl,
        // muss der Kommentar sichtbar bleiben, sonst würde das weiterhin vorhandene Queue-Item
        // den vermeintlich gelöschten Kommentar bei einem späteren Sync wieder erzeugen,
        // obwohl der Fehler-Toast unten dem Nutzer "gelöscht" widersprochen hätte.
        try {
          await removeFromQueue(commentId);
        } catch (removeErr) {
          // Code-Review-Finding (2. Re-Review): die Entfernung oben (VOR dem await) muss
          // rückgängig gemacht werden, sonst verpasst der Deferred-Beobachtungs-Effekt einen
          // späteren, tatsächlich erfolgreichen Hintergrund-Sync desselben (weiterhin
          // existierenden) Queue-Items — der Kommentar bliebe dauerhaft als pending-* stehen
          // und würde bei einem erneuten Bearbeiten fälschlich als neuer rework_comment_add
          // angelegt, statt korrekt auf die echte ID umgehängt zu werden.
          deferredPendingIdsRef.current.add(commentId);
          throw removeErr;
        }
        succeededQueueIdsRef.current.delete(commentId);
        realCommentIdByTempIdRef.current.delete(commentId);
        setComments((prev) => prev.filter((c) => c.id !== commentId));
      } else {
        await deleteReworkCommentAsync(ticketId, commentId);
        setComments((prev) => prev.filter((c) => c.id !== commentId));
      }
      showToast('success', 'Kommentar gelöscht', 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Kommentar konnte nicht gelöscht werden';
      showToast('error', message, 4000);
    } finally {
      setDeletingCommentId(null);
    }
  };

  if (!effectiveIsOpsManager) return null;

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Kommentare</p>

      {loading ? (
        <div className="mt-3 flex justify-center py-3">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-cyan-600" />
        </div>
      ) : comments.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Noch keine Kommentare vorhanden.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {comments.map((comment) => {
            const isEditing = editingCommentId === comment.id;
            const isOwn = Boolean(
              comment.authorEmail &&
                user?.email &&
                comment.authorEmail.trim().toLowerCase() === user.email.trim().toLowerCase()
            );
            const isTemp = comment.id.startsWith(PENDING_ID_PREFIX);
            // Präzises Matching über die ID statt über ticketId+Text (queueKey === comment.id,
            // siehe handleSubmitComment) — robust auch bei zwei Kommentaren mit identischem Text.
            const isPendingSync =
              isTemp &&
              queueItems.some(
                (queueItem) => queueItem.id === comment.id && queueItem.type === 'rework_comment_add'
              );
            // Ein bereits synchronisierter Kommentar braucht für Bearbeiten/Löschen die echte,
            // von HubSpot vergebene Kommentar-ID und funktioniert daher nur online. Ein noch in
            // der Queue wartender (isPendingSync) Kommentar hat dagegen noch gar keine echte
            // HubSpot-Note — hier ändern/entfernen Bearbeiten/Löschen stattdessen direkt den
            // lokalen Queue-Eintrag (kein API-Call nötig, siehe handleSaveEdit/handleDelete und
            // docs/nacharbeit-hubspot-kommentare-plan.md, Abschnitt 4 "Offline-Fähigkeit") und
            // sind deshalb unabhängig vom Online-Status verfügbar. Nur im (kurzen) Übergang
            // zwischen "Queue-Item ist weg" und "Sync-Bestätigungs-Effekt hat es ersetzt" ist
            // isTemp zwar noch true, aber isPendingSync schon false — dann bleiben die Aktionen
            // vorsichtshalber deaktiviert, statt gegen ein nicht mehr existierendes Queue-Item
            // zu operieren.
            const actionsUnavailableReason = isTemp
              ? isPendingSync
                ? null
                : 'Kommentar wird noch synchronisiert'
              : !isOnline
                ? 'Nur online verfügbar'
                : null;

            return (
              <li
                key={comment.id}
                className={`min-w-0 rounded-lg border border-slate-100 px-3 py-2 text-sm dark:border-white/10 ${isPendingSync ? 'opacity-60' : ''}`}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <MentionComposer value={editValue} onChange={handleEditChange} suggestions={ownerSuggestions} autoFocus />
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="text-xs font-semibold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
                      >
                        Abbrechen
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleSaveEdit(comment)}
                        disabled={!editPlainText.trim() || savingEditId === comment.id}
                        className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-cyan-400 dark:hover:text-cyan-300"
                      >
                        {savingEditId === comment.id ? 'Speichert…' : 'Speichern'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="min-w-0 truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
                        {commentAuthorLabel(comment)}
                      </span>
                      <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
                        {formatTimestamp(comment.createdAt)}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words text-slate-800 dark:text-slate-200">
                      {renderCommentText(comment.text, comment.mentionedOwnerIds, ownersById)}
                    </p>
                    {isPendingSync && (
                      <p className="mt-1 text-xs italic text-slate-400 dark:text-slate-500">wird synchronisiert…</p>
                    )}
                    {isOwn && (
                      <div className="mt-1 flex gap-3">
                        <button
                          type="button"
                          onClick={() => startEdit(comment)}
                          disabled={Boolean(actionsUnavailableReason)}
                          title={actionsUnavailableReason ?? undefined}
                          className="text-xs font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-200 dark:decoration-slate-500 dark:hover:text-white"
                        >
                          Bearbeiten
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(comment)}
                          disabled={Boolean(actionsUnavailableReason) || deletingCommentId === comment.id}
                          title={actionsUnavailableReason ?? undefined}
                          className="text-xs font-semibold text-red-600 underline decoration-red-300 underline-offset-2 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:decoration-red-700 dark:hover:text-red-300"
                        >
                          {deletingCommentId === comment.id ? 'Löscht…' : 'Löschen'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 border-t border-slate-100 pt-3 dark:border-white/10">
        <MentionComposer
          value={composeValue}
          onChange={handleComposeChange}
          suggestions={ownerSuggestions}
          placeholder="Kommentar schreiben… @ für Mentions"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={handleSubmitComment}
            disabled={!composePlainText.trim()}
            className="min-h-[40px] rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Kommentar senden
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReworkCommentThread;
